// Static marketing content for the Astria landing page.
//
// Seeded from the approved landing design (AstriaFinal/public/index.html) and
// maintained here by hand. English strings double as translation keys (see
// landingTranslations.ts), so editing one means updating its row there too.

export type LandingLangCode =
  | "en" | "th" | "vi" | "id" | "ms" | "fil" | "ko" | "ja"
  | "zh-CN" | "hi" | "ar" | "es" | "pt-BR" | "fr" | "de";

export interface LandingLanguage {
  code: LandingLangCode;
  nativeName: string;
  englishName: string;
  dir: "ltr" | "rtl";
  /** Pricing region preselected when this language is chosen. */
  defaultRegion: string | null;
}

export interface RegionPrice {
  id: string;
  flag: string;
  country: string;
  currency: string;
  monthly: string;
  yearly: string;
  /** Yearly price expressed per month, e.g. "≈ $10.00". */
  perMonth: string;
  /** Percentage saved by paying yearly. */
  save: number;
}

export interface ToneLane {
  code: string;
  label: string;
  sample: string;
}

export interface TitledItem {
  title: string;
  text?: string;
}

export interface CtaContent {
  label: string;
  action: LandingAction;
}

export interface PlanTier {
  kicker: string;
  /** Present only on the highlighted (Pro) tier. */
  badge?: string;
  name: string;
  tagline: string;
  features: string[];
  cta: CtaContent;
}

export interface ProfileField {
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
}

export type LandingAction = "start_map" | "start_free" | "start_pro" | "learn_more";

export const DEFAULT_LANGUAGE: LandingLangCode = "en";
export const DEFAULT_LANE = "UK";
export const DEFAULT_PRICING_REGION = "US";

export const LANGUAGES: readonly LandingLanguage[] = [
  {
    "code": "en",
    "nativeName": "English",
    "englishName": "English",
    "dir": "ltr",
    "defaultRegion": "US"
  },
  {
    "code": "th",
    "nativeName": "ไทย",
    "englishName": "Thai",
    "dir": "ltr",
    "defaultRegion": "TH"
  },
  {
    "code": "vi",
    "nativeName": "Tiếng Việt",
    "englishName": "Vietnamese",
    "dir": "ltr",
    "defaultRegion": "VN"
  },
  {
    "code": "id",
    "nativeName": "Bahasa Indonesia",
    "englishName": "Indonesian",
    "dir": "ltr",
    "defaultRegion": "ID"
  },
  {
    "code": "ms",
    "nativeName": "Bahasa Melayu",
    "englishName": "Malay",
    "dir": "ltr",
    "defaultRegion": "MY"
  },
  {
    "code": "fil",
    "nativeName": "Filipino",
    "englishName": "Filipino",
    "dir": "ltr",
    "defaultRegion": "PH"
  },
  {
    "code": "ko",
    "nativeName": "한국어",
    "englishName": "Korean",
    "dir": "ltr",
    "defaultRegion": "KR"
  },
  {
    "code": "ja",
    "nativeName": "日本語",
    "englishName": "Japanese",
    "dir": "ltr",
    "defaultRegion": "JP"
  },
  {
    "code": "zh-CN",
    "nativeName": "中文",
    "englishName": "Chinese (Simplified)",
    "dir": "ltr",
    "defaultRegion": "SG"
  },
  {
    "code": "hi",
    "nativeName": "हिन्दी",
    "englishName": "Hindi",
    "dir": "ltr",
    "defaultRegion": "IN"
  },
  {
    "code": "ar",
    "nativeName": "العربية",
    "englishName": "Arabic",
    "dir": "rtl",
    "defaultRegion": "GCC"
  },
  {
    "code": "es",
    "nativeName": "Español",
    "englishName": "Spanish",
    "dir": "ltr",
    "defaultRegion": "ES"
  },
  {
    "code": "pt-BR",
    "nativeName": "Português",
    "englishName": "Portuguese",
    "dir": "ltr",
    "defaultRegion": "BR"
  },
  {
    "code": "fr",
    "nativeName": "Français",
    "englishName": "French",
    "dir": "ltr",
    "defaultRegion": null
  },
  {
    "code": "de",
    "nativeName": "Deutsch",
    "englishName": "German",
    "dir": "ltr",
    "defaultRegion": null
  }
];

/** Tone lane previewed when a language is chosen. */
export const LANG_TO_LANE: Readonly<Record<string, string>> = {
  "en": "UK",
  "th": "TH",
  "ja": "JP",
  "ko": "KR",
  "pt-BR": "BR",
  "es": "ES",
  "hi": "IN",
  "ar": "GCC",
  "ms": "MY",
  "zh-CN": "SG"
};

/** Display prices per country. Marketing copy only — checkout prices come from the billing backend. */
export const REGION_PRICES: readonly RegionPrice[] = [
  {
    "id": "US",
    "flag": "🇺🇸",
    "country": "United States",
    "currency": "USD",
    "monthly": "$14.99",
    "yearly": "$119.99",
    "perMonth": "≈ $10.00",
    "save": 33
  },
  {
    "id": "UK",
    "flag": "🇬🇧",
    "country": "United Kingdom",
    "currency": "GBP",
    "monthly": "£11.99",
    "yearly": "£99.99",
    "perMonth": "≈ £8.33",
    "save": 31
  },
  {
    "id": "CA",
    "flag": "🇨🇦",
    "country": "Canada",
    "currency": "CAD",
    "monthly": "C$12.99",
    "yearly": "C$109.99",
    "perMonth": "≈ C$9.17",
    "save": 29
  },
  {
    "id": "TH",
    "flag": "🇹🇭",
    "country": "Thailand",
    "currency": "THB",
    "monthly": "฿299",
    "yearly": "฿2,490",
    "perMonth": "≈ ฿208",
    "save": 31
  },
  {
    "id": "VN",
    "flag": "🇻🇳",
    "country": "Vietnam",
    "currency": "VND",
    "monthly": "149.000₫",
    "yearly": "1.249.000₫",
    "perMonth": "≈ 104.000₫",
    "save": 30
  },
  {
    "id": "ID",
    "flag": "🇮🇩",
    "country": "Indonesia",
    "currency": "IDR",
    "monthly": "Rp 79.000",
    "yearly": "Rp 649.000",
    "perMonth": "≈ Rp 54.000",
    "save": 32
  },
  {
    "id": "PH",
    "flag": "🇵🇭",
    "country": "Philippines",
    "currency": "PHP",
    "monthly": "₱249",
    "yearly": "₱1,990",
    "perMonth": "≈ ₱166",
    "save": 33
  },
  {
    "id": "MY",
    "flag": "🇲🇾",
    "country": "Malaysia",
    "currency": "MYR",
    "monthly": "RM19",
    "yearly": "RM159",
    "perMonth": "≈ RM13.25",
    "save": 30
  },
  {
    "id": "SG",
    "flag": "🇸🇬",
    "country": "Singapore",
    "currency": "SGD",
    "monthly": "S$7.99",
    "yearly": "S$64.99",
    "perMonth": "≈ S$5.42",
    "save": 32
  },
  {
    "id": "KR",
    "flag": "🇰🇷",
    "country": "Korea",
    "currency": "KRW",
    "monthly": "₩7,900",
    "yearly": "₩64,900",
    "perMonth": "≈ ₩5,408",
    "save": 32
  },
  {
    "id": "JP",
    "flag": "🇯🇵",
    "country": "Japan",
    "currency": "JPY",
    "monthly": "¥900",
    "yearly": "¥7,500",
    "perMonth": "≈ ¥625",
    "save": 31
  },
  {
    "id": "IN",
    "flag": "🇮🇳",
    "country": "India",
    "currency": "INR",
    "monthly": "₹299",
    "yearly": "₹2,499",
    "perMonth": "≈ ₹208",
    "save": 30
  },
  {
    "id": "GCC",
    "flag": "🌍",
    "country": "GCC (USD)",
    "currency": "USD",
    "monthly": "$12.99",
    "yearly": "$109.99",
    "perMonth": "≈ $9.17",
    "save": 29
  },
  {
    "id": "ES",
    "flag": "🇪🇸",
    "country": "Spain",
    "currency": "EUR",
    "monthly": "7,99 €",
    "yearly": "64,99 €",
    "perMonth": "≈ 5,42 €",
    "save": 32
  },
  {
    "id": "BR",
    "flag": "🇧🇷",
    "country": "Brazil",
    "currency": "BRL",
    "monthly": "R$ 24",
    "yearly": "R$ 199",
    "perMonth": "≈ R$ 16,58",
    "save": 31
  },
  {
    "id": "MX",
    "flag": "🇲🇽",
    "country": "Mexico",
    "currency": "MXN",
    "monthly": "MXN $129",
    "yearly": "MXN $1,049",
    "perMonth": "≈ MXN $87",
    "save": 32
  },
  {
    "id": "AR",
    "flag": "🇦🇷",
    "country": "Argentina",
    "currency": "ARS",
    "monthly": "ARS $6.900",
    "yearly": "ARS $56.900",
    "perMonth": "≈ ARS $4.742",
    "save": 31
  },
  {
    "id": "CO",
    "flag": "🇨🇴",
    "country": "Colombia",
    "currency": "COP",
    "monthly": "COP $24.900",
    "yearly": "COP $199.900",
    "perMonth": "≈ COP $16.658",
    "save": 33
  },
  {
    "id": "CL",
    "flag": "🇨🇱",
    "country": "Chile",
    "currency": "CLP",
    "monthly": "CLP $6.900",
    "yearly": "CLP $56.900",
    "perMonth": "≈ CLP $4.742",
    "save": 31
  },
  {
    "id": "PE",
    "flag": "🇵🇪",
    "country": "Peru",
    "currency": "PEN",
    "monthly": "S/24",
    "yearly": "S/199",
    "perMonth": "≈ S/16.58",
    "save": 31
  }
];

export const CORE_PRINCIPLES: readonly string[] = [
  "Personal Memory",
  "Deep Astrology",
  "Local Cultural Context",
  "Current Timing",
  "Life Context",
  "Practical Guidance"
];

export const TONE_LANES: readonly ToneLane[] = [
  {
    "code": "UK",
    "label": "Clarity",
    "sample": "Pace is slowing and clarity is rising. Pick one priority and finish it before noon."
  },
  {
    "code": "TH",
    "label": "Soft Direct",
    "sample": "Your pace is gently slowing today. It may help to finish one important thing first — then let yourself rest."
  },
  {
    "code": "JP",
    "label": "Precision",
    "sample": "Pace: easing. Clarity: rising. Recommended: one focused task, 09:00–11:00. Keep the afternoon light."
  },
  {
    "code": "KR",
    "label": "Calm Structure",
    "sample": "Today moves slower, and that is fine. Morning: one key task. Afternoon: review. Evening: rest."
  },
  {
    "code": "BR",
    "label": "Warm Direct",
    "sample": "Hey — your rhythm is asking for a slower day. Do the one thing that matters, then be kind to yourself."
  },
  {
    "code": "ES",
    "label": "Emotive Clarity",
    "sample": "Something in you wants to slow down — listen to it. Clarity is arriving; give it room with one clear choice."
  },
  {
    "code": "IN",
    "label": "Reflective",
    "sample": "A quieter rhythm today. Before acting, ask what truly needs your attention — the answer will come more easily than usual."
  },
  {
    "code": "GCC",
    "label": "Formal Soft",
    "sample": "Your rhythm today suggests a measured pace. We recommend completing one priority with care and reserving the evening for rest."
  },
  {
    "code": "SG",
    "label": "Neutral Precision",
    "sample": "Pace lower, clarity higher. Focus on one priority this morning; keep the afternoon light."
  },
  {
    "code": "MY",
    "label": "Neutral Precision",
    "sample": "Pace lower, clarity higher. Focus on one priority this morning; keep the afternoon light."
  }
];
export const LANE_LOGIC: string = "Guidance adapts to tone, pacing, and cultural communication norms of each lane.";
export const LANE_DEMO_CONTEXT: string = "Same moment · your tone";
export const LIFESTYLE_COVERAGE: readonly string[] = [
  "Relationships",
  "Family",
  "Work",
  "Money",
  "Health",
  "Decision-making",
  "Personal growth"
];

export const HERO = {
  "eyebrow": "One Astria · Your language, your culture, your story",
  "headline": "Astria — Your Personal Intelligence Companion",
  "subheadline": "Guidance that becomes more personal the more Astria understands you.",
  "body": "Local wisdom and deep astrology, translated into practical guidance.",
  "ctaPrimary": {
    "label": "Meet your Astria",
    "action": "start_map"
  },
  "ctaSecondary": {
    "label": "How it works",
    "action": "learn_more"
  }
} as const;

export const UNIQUE_MAP = {
  "eyebrow": "Inside Astria",
  "body": "A few calm places, one companion that remembers what matters to you.",
  "items": [
    {
      "title": "Today",
      "text": "What matters today, why it matters for you, and what to do next."
    },
    {
      "title": "Life GPS",
      "text": "How to move through your current phase — not how well you are performing."
    },
    {
      "title": "Life Areas",
      "text": "Foundations, Expansion and Expression, shown as calm states."
    },
    {
      "title": "Ask Astria",
      "text": "Ask anything. Concise by default, deeper whenever you want."
    },
    {
      "title": "Journey",
      "text": "Your personal timeline of chapters, decisions and moments."
    }
  ]
};
export const PERSONAL_GUIDANCE = {
  "eyebrow": "Culturally native",
  "body": "Astria shapes its language, terminology and tone to where you live — felt in every sentence, never a label on the screen.",
  "items": [
    "Your language, your terms",
    "Your pace and timing",
    "Your story, your step"
  ]
};
export const LIFE_NAVIGATION = {
  "eyebrow": "How Astria thinks",
  "body": "Memory, astrology and local culture come together as one practical next step.",
  "pillars": [
    {
      "title": "Personal memory",
      "text": "Astria understands you more over time — and you control what it remembers."
    },
    {
      "title": "Deep astrology",
      "text": "Your chart and current timing, always there for those who want to go deeper."
    },
    {
      "title": "Local wisdom",
      "text": "Cultural nuance without stereotyping, in your own language."
    },
    {
      "title": "Practical guidance",
      "text": "Clear next steps connected to your real life."
    }
  ]
};
export const CALM_GUIDANCE = {
  "moments": [
    {
      "label": "☀ Morning brief",
      "text": "A 30-second read of today's rhythm before the day begins."
    },
    {
      "label": "◐ Timing nudge",
      "text": "A quiet heads-up when a good window opens."
    },
    {
      "label": "☾ Evening reflection",
      "text": "One question to close the day with clarity."
    }
  ]
};
export const INNER_WORLD = {
  "eyebrow": "Life Areas",
  "body": "Each area is shown as a calm state — never a score to chase.",
  "metrics": [
    {
      "label": "Foundations",
      "sampleValue": "Stable",
      "description": "The ground you stand on: health, home, money and daily rhythm."
    },
    {
      "label": "Expansion",
      "sampleValue": "Balanced",
      "description": "Where you are growing: work, learning and new directions."
    },
    {
      "label": "Expression",
      "sampleValue": "Strong",
      "description": "How you connect and create: relationships and self-expression."
    },
    {
      "label": "Rest",
      "sampleValue": "Needs Care",
      "description": "How well you recover: sleep, pauses and emotional breathing room."
    }
  ]
};
export const STEPS_TO_CLARITY = {
  "eyebrow": "How it works",
  "steps": [
    {
      "number": "01",
      "title": "Tell Astria about you",
      "text": "Share when and where you were born."
    },
    {
      "number": "02",
      "title": "See your Life GPS",
      "text": "Understand the phase you are in right now."
    },
    {
      "number": "03",
      "title": "Take one next step",
      "text": "Small, practical and connected to your life."
    },
    {
      "number": "04",
      "title": "Astria learns with you",
      "text": "Guidance grows more personal, with you in control."
    }
  ]
};
export const PROFILE: {
  eyebrow: string;
  body: string;
  fields: ProfileField[];
  cta: CtaContent;
  successMessage: string;
  note: string;
} = {
  "eyebrow": "Start here",
  "body": "Four details are all it takes. Your map is private, and you can edit it anytime.",
  "fields": [
    {
      "label": "Name",
      "type": "text",
      "required": true,
      "placeholder": "Your first name"
    },
    {
      "label": "Date of birth",
      "type": "date",
      "required": true
    },
    {
      "label": "Time of birth",
      "type": "time",
      "required": false
    },
    {
      "label": "Location",
      "type": "text",
      "required": true,
      "placeholder": "City of birth"
    }
  ],
  "cta": {
    "label": "Create profile",
    "action": "start_map"
  },
  "successMessage": "✓ Profile created — your map is on its way",
  "note": "No birth time? That's fine — you can add it later."
};
export const DEEP_UNDERSTANDING = {
  "title": "What Astria understands about you.",
  "items": [
    {
      "title": "Personalized with",
      "text": "Every answer shows which parts of your context it used."
    },
    {
      "title": "Correct, edit or forget",
      "text": "See what Astria knows about you and change it anytime."
    },
    {
      "title": "Pause memory",
      "text": "Step back whenever you like — Astria still helps."
    }
  ]
};
export const DOMAINS = {
  "eyebrow": "Emotional tools",
  "items": [
    {
      "title": "Tarot Reflection",
      "text": "A gentle prompt for reflection, in select markets."
    },
    {
      "title": "Letter Never Sent",
      "text": "A private place to release what you never said, in select markets."
    },
    {
      "title": "Journey",
      "text": "Your personal timeline, feeding the same Astria when you allow it."
    }
  ]
};
export const GLOBAL_LANGUAGE = {
  "eyebrow": "Global by design",
  "items": [
    {
      "title": "Core language",
      "text": "The interface and your guidance, in your own language."
    },
    {
      "title": "Guidance language",
      "text": "Advice written the way people speak where you live."
    },
    {
      "title": "Worldview framing",
      "text": "Local astrology traditions and ideas that make sense to you."
    }
  ],
  "footnote": "Cultural nuance without stereotyping — no fear, no fake certainty."
};
export const PRIVACY = {
  "eyebrow": "Trust",
  "items": [
    {
      "title": "Private by default",
      "text": "Birth data is encrypted and never sold. Export or delete it anytime."
    },
    {
      "title": "Personalization, explained",
      "text": "Astria shows what it used, and you control your memory."
    },
    {
      "title": "No fear, no fake certainty",
      "text": "Guidance that leaves the choice with you and never claims to know the future."
    }
  ]
};
export const PLANS: {
  eyebrow: string;
  body: string;
  fineprint: string;
  tiers: PlanTier[];
} = {
  "eyebrow": "Plans",
  "body": "Local pricing in 20 countries.",
  "fineprint": "7-day free trial · Cancel anytime · Prices in local currency",
  "tiers": [
    {
      "kicker": "Free",
      "name": "Start with Astria",
      "tagline": "Everything you need to begin.",
      "features": [
        "Today's guidance",
        "Life GPS preview",
        "Basic memory",
        "All 15 languages"
      ],
      "cta": {
        "label": "Get started",
        "action": "start_free"
      }
    },
    {
      "kicker": "Pro · Your Life GPS",
      "badge": "Most loved",
      "name": "Your full Life GPS",
      "tagline": "The complete navigation system for your life.",
      "features": [
        "Full Life GPS and Life Areas",
        "Deep astrology on request",
        "Memory that grows with you",
        "Private by default"
      ],
      "cta": {
        "label": "Unlock Your Life GPS",
        "action": "start_pro"
      }
    }
  ]
};
export const FAQ = {
  "eyebrow": "Questions",
  "items": [
    {
      "q": "Does Astria predict the future?",
      "a": "No. Astria offers perspective and timing, never fear-based predictions or fake certainty. You stay in charge of your next step."
    },
    {
      "q": "What does Astria remember about me?",
      "a": "Only what helps. You can view, correct, forget or pause your memory anytime, and export or delete your data."
    },
    {
      "q": "What if I don't know my birth time?",
      "a": "You can still use Astria. Some details will be broader, and you can add your time whenever you find it."
    },
    {
      "q": "Can I use Astria in my language?",
      "a": "Yes. Astria works in 15 languages and adapts its tone and terms to where you live."
    },
    {
      "q": "Can I cancel anytime?",
      "a": "Always. Cancel in a tap and keep access until the end of your billing period."
    }
  ]
};
export const FINAL_CTA = {
  "eyebrow": "Your Astria",
  "cta": {
    "label": "Get started",
    "action": "start_map"
  }
};
export const FOOTER_NOTE: string = "© 2026 Astria · For reflection and self-discovery, not professional advice.";
